import React from "react";
import { Card, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import { normalizeWorkspaceDevices } from "app/pages/customers/dsok/utils/normalizeDsokWorkspaces";

/**
 * One workspace row — layout aligned with AccessSystem VoucherSeriesListItem (Card, px:3, responsive columns).
 */
const PosWorkspaceListItem = ({ row, onEdit }) => {
  const { t } = useTranslation();
  const devices = normalizeWorkspaceDevices(row?.devices);
  const n = devices.length;

  const statusChip =
    row?.isActive === true ? (
      <Chip
        size="small"
        label={t("pages.pos.workspaces.statusActive")}
        color="success"
      />
    ) : row?.isActive === false ? (
      <Chip
        size="small"
        label={t("pages.pos.workspaces.statusInactive")}
        sx={{
          bgcolor: "grey.400",
          color: "grey.900",
          "& .MuiChip-label": { px: 1 },
        }}
      />
    ) : (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );

  return (
    <Card sx={{ mb: 1 }}>
      <Div
        sx={{
          px: 3,
          py: 1.5,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          flexWrap: "wrap",
          rowGap: { xs: 1.5, md: 0 },
        }}
      >
        <Div sx={{ flex: { xs: "1 1 100%", md: 1.2 }, minWidth: 0, px: 1 }}>
          <Typography
            fontSize="12px"
            variant="h6"
            color="text.secondary"
            mb={0.25}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            {t("pages.pos.workspaces.colName")}
          </Typography>
          <Typography variant="body1">
            {row?.name != null ? String(row.name) : "—"}
          </Typography>
        </Div>

        <Div sx={{ flex: { xs: "1 1 100%", md: 1.2 }, minWidth: 0, px: 1 }}>
          <Typography
            fontSize="12px"
            variant="h6"
            color="text.secondary"
            mb={0.25}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            {t("pages.pos.workspaces.colUuid")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
          >
            {row?.uuid != null ? String(row.uuid) : "—"}
          </Typography>
        </Div>

        <Div sx={{ flex: { xs: "1 1 100%", md: 0.7 }, minWidth: 0, px: 1 }}>
          <Typography
            fontSize="12px"
            variant="h6"
            color="text.secondary"
            mb={0.25}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            {t("pages.pos.workspaces.colDevices")}
          </Typography>
          <Typography variant="body1">
            {t("pages.pos.workspaces.deviceCount", { count: n })}
          </Typography>
        </Div>

        <Div sx={{ flex: { xs: "1 1 100%", md: 0.9 }, minWidth: 0, px: 1 }}>
          <Typography
            fontSize="12px"
            variant="h6"
            color="text.secondary"
            mb={0.25}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            {t("pages.pos.workspaces.colStatus")}
          </Typography>
          {statusChip}
        </Div>

        <Div
          sx={{
            flex: { xs: "1 1 auto", md: 0 },
            px: 1,
            alignSelf: { xs: "flex-end", md: "center" },
          }}
        >
          <Typography
            fontSize="12px"
            variant="h6"
            color="text.secondary"
            mb={0.25}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            {t("pages.pos.users.colActions")}
          </Typography>
          <Div
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 0.5,
              minWidth: 40,
            }}
          >
            <Tooltip title={t("buttons.edit")} placement="top-start">
              <IconButton
                size="small"
                onClick={() => onEdit(row)}
                aria-label={t("buttons.edit")}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Div>
        </Div>
      </Div>
    </Card>
  );
};

export default PosWorkspaceListItem;
